'use client'

import { useState, useMemo } from 'react'
import { ChevronDown, Search } from 'lucide-react'

interface VbplItem {
  hsId: string
  vbId: string
  hsCode: string
  vbpl: {
    id: string
    name: string
    url: string
    dateIssued: string
    dateEffective: string
    createdAt: string
    type?: string
  }
}

interface VbplTableProps {
  vbplList: VbplItem[]
}

const TABS = [
  { id: 'all', label: 'Tất cả' },
  { id: 'Phân tích phân loại', label: 'Phân tích phân loại' },
  { id: 'TCVN & QCVN', label: 'TCVN & QCVN' },
  { id: 'Khác', label: 'Khác' },
]

export default function VbplTable({ vbplList }: VbplTableProps) {
  const [activeTab, setActiveTab] = useState('all')
  const [searchTerm, setSearchTerm] = useState('')
  const [sortOrder, setSortOrder] = useState<'newest' | 'oldest'>('newest')
  const [showAll, setShowAll] = useState(false)

  const INITIAL_SHOW_COUNT = 5

  const classifyVbpl = (type?: string) => {
    if (!type) return 'Khác'
    if (type === 'TCVN/QCVN' || type === 'TCVN & QCVN') return 'TCVN & QCVN'
    if (type === 'Văn bản khác') return 'Phân tích phân loại'
    return 'Khác'
  }

  const tabCounts = useMemo(() => {
    const counts = {
      'all': vbplList.length,
      'Phân tích phân loại': 0,
      'TCVN & QCVN': 0,
      'Khác': 0,
    }
    vbplList.forEach(item => {
      const category = classifyVbpl(item.vbpl?.type)
      if (counts[category as keyof typeof counts] !== undefined) {
        counts[category as keyof typeof counts]++
      }
    })
    return counts
  }, [vbplList])

  const filteredAndSortedList = useMemo(() => {
    let result = vbplList

    if (activeTab !== 'all') {
      result = result.filter(item => classifyVbpl(item.vbpl?.type) === activeTab)
    }

    if (searchTerm) {
      const lowerSearch = searchTerm.toLowerCase()
      result = result.filter(item =>
        item.vbpl?.name?.toLowerCase().includes(lowerSearch)
      )
    }

    result = [...result].sort((a, b) => {
      const dateA = a.vbpl?.dateIssued ? new Date(a.vbpl.dateIssued).getTime() : 0
      const dateB = b.vbpl?.dateIssued ? new Date(b.vbpl.dateIssued).getTime() : 0
      if (sortOrder === 'newest') {
        return dateB - dateA
      } else {
        return dateA - dateB
      }
    })

    return result
  }, [vbplList, activeTab, searchTerm, sortOrder])

  const visibleTabs = TABS.filter(
    tab => tab.id === 'all' || tabCounts[tab.id as keyof typeof tabCounts] > 0
  )

  const displayedList = showAll
    ? filteredAndSortedList
    : filteredAndSortedList.slice(0, INITIAL_SHOW_COUNT)

  const hasMore = filteredAndSortedList.length > INITIAL_SHOW_COUNT

  return (
    <div className="flex flex-col gap-4 mt-2">
      {/* Search, Tabs, and Sort */}
      <div className="flex flex-col gap-4">
        <div className="flex flex-col xl:flex-row xl:items-center justify-between gap-4 border-b border-gray-100 pb-4">
          {/* Tabs */}
          <div className="flex flex-wrap items-center gap-5">
            {visibleTabs.map(tab => (
              <label
                key={tab.id}
                className={`flex items-center gap-2 cursor-pointer text-sm font-medium ${activeTab === tab.id ? 'text-blue-600' : 'text-gray-600 hover:text-gray-900'
                  }`}
              >
                <input
                  type="radio"
                  name="vbpl_tab"
                  value={tab.id}
                  checked={activeTab === tab.id}
                  onChange={() => {
                    setActiveTab(tab.id)
                    setShowAll(false)
                  }}
                  className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500 cursor-pointer"
                />
                <span className="flex items-center">
                  {tab.label}
                  <span
                    className={`ml-1.5 px-2 py-0.5 rounded-full text-xs ${activeTab === tab.id ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'
                      }`}
                  >
                    {tabCounts[tab.id as keyof typeof tabCounts]}
                  </span>
                </span>
              </label>
            ))}
          </div>

          {/* Sort Menu */}
          <div className="shrink-0 w-36">
            <select
              title="Sắp xếp"
              value={sortOrder}
              onChange={(e) => {
                setSortOrder(e.target.value as 'newest' | 'oldest')
                setShowAll(false)
              }}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="newest">Mới nhất</option>
              <option value="oldest">Cũ nhất</option>
            </select>
          </div>
        </div>

        {/* Search */}
        <div className="relative max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Tìm trong danh sách..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value)
              setShowAll(false)
            }}
            className="w-full pl-9 pr-4 py-2 text-sm border border-gray-300 bg-gray-50 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-colors"
          />
        </div>
      </div>
      <table className="policy-table w-full text-left">
        <thead>
          <tr>
            <th className="py-2 text-gray-500 font-medium">VĂN BẢN</th>
            <th className="py-2 text-gray-500 font-medium" style={{ width: '180px' }}>NGÀY BAN HÀNH</th>
          </tr>
        </thead>
        <tbody>
          {displayedList.map((item: VbplItem) => {
            if (!item.vbpl) return null

            const formattedDate = item.vbpl.dateIssued
              ? new Date(item.vbpl.dateIssued).toLocaleDateString('vi-VN')
              : 'N/A'

            return (
              <tr key={item.vbId} className="border-t border-gray-100">
                <td className="py-3">
                  <a
                    href={createVbplLink(item.vbpl)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="policy-name text-blue-600 hover:underline"
                  >
                    {item.vbpl.name}
                  </a>
                </td>
                <td className="policy-date py-3 text-sm text-gray-600 font-medium">{formattedDate}</td>
              </tr>
            )
          })}
        </tbody>
      </table>

      {filteredAndSortedList.length === 0 && (
        <div className="py-8 text-center text-gray-500">
          Không tìm thấy văn bản nào phù hợp.
        </div>
      )}

      {hasMore && !showAll && (
        <div className="flex justify-center mt-4">
          <button
            onClick={() => setShowAll(true)}
            className="inline-flex items-center gap-2 px-6 py-2 text-sm font-medium text-blue-600 bg-white border border-blue-600 rounded-lg hover:bg-blue-50 transition-colors"
          >
            Xem tất cả {filteredAndSortedList.length} văn bản
            <ChevronDown className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  )
}

function createVbplLink(vbpl: VbplItem['vbpl']) {
  return `https://caselaw.vn/van-ban-phap-luat/${vbpl.id}-${vbpl.url}`
}
